import { inject, injectable } from "@launchtray/tsyringe-async";
import { Bindable, NotifyProperty, nameof } from "bindable-data";
import { IMarketplaceMapModel } from "./IMarketplaceMapModel";
import { MapArea } from "./MapArea";
import { ZipCode } from "./ZipCode";
import { INetworkClient } from "../../../../../../networking/INetworkClient";
import { MarketplaceListItem } from "./MarketplaceListItem";
import { MapFilter } from "./MapFilter";
import { MarketplaceMapCache } from "./cache/MarketplaceMapCache";

@injectable()
export class MarketplaceMapModel extends Bindable implements IMarketplaceMapModel {
    public readonly areas: NotifyProperty<MapArea[]>;
    public readonly zipCodes: NotifyProperty<ZipCode[]>;
    public readonly buildings: NotifyProperty<MarketplaceListItem[]>;
    private readonly networkClient: INetworkClient;
    private marketplaceMapCache: MarketplaceMapCache;

    public constructor(
        @inject("INetworkClient") networkClient: INetworkClient
    ) {
        super();
        this.networkClient = networkClient;
        this.areas = new NotifyProperty<MapArea[]>(this, nameof(this, "areas"), new Array<MapArea>());
        this.zipCodes = new NotifyProperty<ZipCode[]>(this, nameof(this, "zipCodes"), new Array<ZipCode>());
        this.buildings = new NotifyProperty<MarketplaceListItem[]>(this, nameof(this, "buildings"), new Array<MarketplaceListItem>());
        this.marketplaceMapCache = new MarketplaceMapCache();
    }

    public async updateAreas(returnOnlyBuildingsByMon: boolean = false): Promise<void> {
        const areas = await this.queryAreas(returnOnlyBuildingsByMon);
        this.areas.setValue(areas);
    }

    private async queryAreas(returnOnlyBuildingsByMon: boolean = false): Promise<MapArea[]> {
        const mapPointsResponse = await this.networkClient.getMarketplaceMapAreas(returnOnlyBuildingsByMon);
        const areasDto = mapPointsResponse.areas;
        if (areasDto == undefined || !(areasDto instanceof Array)) {
            throw new Error("Failed to query areas. No areas in response.");
        }
        const mapAreas = new Array<MapArea>();
        for (const areaDto of areasDto) {
            mapAreas.push(new MapArea(areaDto));
        }
        return mapAreas;
    }

    private async queryZipCodes(area: number, returnOnlyBuildingsByMon: boolean = false): Promise<ZipCode[]> {
        const mapPointsResponse = await this.networkClient.getMarketplaceZipCodes(area, returnOnlyBuildingsByMon);
        const zipCodesDto = mapPointsResponse.zipCodes;
        if (zipCodesDto == undefined || !(zipCodesDto instanceof Array)) {
            throw new Error(`Failed to query area info ${area}. No zipCodes in response.`);
        }
        const mapZipCodes = new Array<ZipCode>();
        for (const zipCodeDto of zipCodesDto) {
            mapZipCodes.push(new ZipCode(zipCodeDto));
        }
        return mapZipCodes;
    }

    private async queryBuildings(filter: MapFilter): Promise<MarketplaceListItem[]> {
        const buildingsResponse = await this.networkClient.getMarketplaceBuildings(filter);
        const listItemsDto = buildingsResponse.items;
        if (listItemsDto == undefined || !(listItemsDto instanceof Array)) {
            throw new Error(`Failed to queryBuildings. Check filter ${filter} or ask support for assistance.`);
        }
        const buildingItems = new Array<MarketplaceListItem>();
        for (const itemDto of listItemsDto) {
            buildingItems.push(new MarketplaceListItem(itemDto));
        }
        return buildingItems;
    }

    public async updateMapFilter(value: MapFilter, force: boolean = false): Promise<void> {
        if (isNaN(value.area)) {
            throw new Error(`Failed to apply map filter:\n${value}. At least area should be defined.`);
        }
        let queryZipCodes = value.zipCode === undefined;
        let queryBuildings = true;
        const tasks = [];
        if (!queryZipCodes && !queryBuildings) {
            return;
        }
        if (queryZipCodes) {
            const cachedZipCodes: ZipCode[] = force ? [] : this.marketplaceMapCache.getCachedData(ZipCode, value);
            if (cachedZipCodes.length > 0) {
                tasks.push(Promise.resolve(cachedZipCodes));
            } else {
                tasks.push(this.queryZipCodes(value.area, value.returnOnlyBuildingsByMon));
            }
        }
        if (queryBuildings) {
            const cachedBuildings: MarketplaceListItem[] = force ? [] : this.marketplaceMapCache.getCachedData(MarketplaceListItem, value);
            if (cachedBuildings.length > 0) {
                tasks.push(Promise.resolve(cachedBuildings));
            } else {
                tasks.push(this.queryBuildings(value));
            }
        }
        try {
            const results = await Promise.all(tasks);
            let zipCodes: ZipCode[];
            let buildings: MarketplaceListItem[];
            if (queryZipCodes && queryBuildings) {
                zipCodes = results[0] as ZipCode[];
                buildings = results[1] as MarketplaceListItem[];
                this.marketplaceMapCache.updateCache(ZipCode, value, zipCodes, force);
                this.marketplaceMapCache.updateCache(MarketplaceListItem, value, buildings, force);
                this.zipCodes.setValue(zipCodes);
                this.buildings.setValue(buildings);
            } else if (queryBuildings) {
                buildings = results[0] as MarketplaceListItem[];
                this.marketplaceMapCache.updateCache(MarketplaceListItem, value, buildings, force);
                this.buildings.setValue(buildings);
            } else {
                zipCodes = results[0] as ZipCode[];
                this.marketplaceMapCache.updateCache(ZipCode, value, zipCodes, force);
                this.zipCodes.setValue(zipCodes);
            }
        } catch (error) {
            throw error;
        }
    }

}
